// src/pages/Admin/create/deal/DealForm.tsx
import React from "react";
import { Env } from "../../shared";
import { useAdminFlag } from "../../deals/featureFlags";
import { FieldLabel, inputClass } from "../shared/FieldLabel";
import { BusinessPicker } from "../shared/BusinessPicker";
import { DateTimeField } from "../shared/DateTimeField";
import { AudienceSelector } from "../shared/AudienceSelector";
import { RecurrenceSection } from "../shared/RecurrenceSection";
import { DealForm as DealFormState } from "./useDealFormState";
import { resolveClosingEnd, conflictingClosingTimes } from "../shared/closingTime";

export function DealForm({
  env, form, setField,
}: {
  env: Env;
  form: DealFormState;
  setField: <K extends keyof DealFormState>(field: K, value: DealFormState[K]) => void;
}) {
  const tz = form.business?.timezone ?? null;
  const RECURRING_DEALS_ENABLED = useAdminFlag('recurring-deals-enabled', env);

  // "End at closing" resolves the venue's close for the day the deal STARTS and
  // writes it into End Time as a normal value the user can still edit. Nothing
  // stores the intent, so the deals list shows a concrete time, not "closing".
  const closing = resolveClosingEnd(form.startTime, form.business?.hours);
  const closingDisabledReason = !form.business
    ? "Pick a business first."
    : closing.error;
  const applyClosingTime = () => {
    if (closing.endTime) setField("endTime", closing.endTime);
  };

  // Resolving once is correct for a single-weekday series, and for any series
  // whose days close at the same time. Say so plainly when they don't.
  const closingConflicts = RECURRING_DEALS_ENABLED
    ? conflictingClosingTimes(form.recurrenceDays, form.business?.hours)
    : [];
  return (
    <div className="space-y-4">
      <BusinessPicker env={env} value={form.business} onChange={b => setField("business", b)} />

      <div>
        <FieldLabel required>Title</FieldLabel>
        <input
          type="text"
          value={form.title}
          onChange={e => setField("title", e.target.value.slice(0, 50))}
          placeholder="Deal title"
          className={inputClass}
          maxLength={50}
        />
        <p className="text-xs text-gray-500 mt-1">{form.title.length}/50</p>
      </div>

      <div>
        <FieldLabel required>Description</FieldLabel>
        <textarea
          value={form.description}
          onChange={e => setField("description", e.target.value.slice(0, 200))}
          placeholder="Describe the deal"
          className={`${inputClass} resize-none`}
          rows={3}
          maxLength={200}
        />
        <p className="text-xs text-gray-500 mt-1">{form.description.length}/200</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <DateTimeField label="Start Time" required value={form.startTime} onChange={v => setField("startTime", v)} />
        <div>
          <DateTimeField label="End Time" required value={form.endTime} onChange={v => setField("endTime", v)} />
          <button
            type="button"
            onClick={applyClosingTime}
            disabled={Boolean(closingDisabledReason)}
            title={closingDisabledReason || `Set end to ${closing.endTime?.slice(11)} — this venue's closing time that day`}
            className="mt-1.5 inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium text-violet-300 bg-violet-950/40 border border-violet-800/50 rounded-lg hover:bg-violet-900/40 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
          >
            🕒 End at closing
          </button>
          {closingDisabledReason && form.startTime && (
            <p className="text-xs text-gray-500 mt-1">{closingDisabledReason}</p>
          )}
        </div>
      </div>
      {!tz && (
        <p className="text-xs text-amber-400">⚠ Pick a business to set the timezone for these times.</p>
      )}
      {closingConflicts.length > 0 && (
        <p className="text-xs text-amber-400">
          ⚠ The days you picked close at different times (
          {closingConflicts.map(c => `${c.day} ${c.close}`).join(", ")}
          ). End Time is one fixed value for every occurrence — split the series or set it by hand.
        </p>
      )}

      <div>
        <FieldLabel required>Notify</FieldLabel>
        <AudienceSelector selected={form.notificationAudience} onChange={next => setField("notificationAudience", next)} />
      </div>

      {RECURRING_DEALS_ENABLED && (
        <RecurrenceSection
          value={{
            recurrenceEnabled: form.recurrenceEnabled,
            recurrenceDays: form.recurrenceDays,
            recurrenceHasEndDate: form.recurrenceHasEndDate,
            recurrenceEndDate: form.recurrenceEndDate,
            startTime: form.startTime,
            timezone: form.business?.timezone ?? undefined,
          }}
          set={(field, v) => setField(field as keyof DealFormState, v as never)}
        />
      )}
    </div>
  );
}
